import React, { useRef, useEffect, useMemo } from 'react'
import ApexCharts from 'apexcharts'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'

export default function TrendChart({ data = [], xField = 'date', series = [], title, height = 320, loading }) {
  /* series shape: [{ key: 'ndrRaised', label: 'NDR Raised', color: '#0F4C81', type: 'area' }] */

  const chartRef = useRef(null)
  const chartInstance = useRef(null)

  const options = useMemo(() => ({
    chart: {
      type: 'area',
      height,
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: "'Inter', sans-serif",
    },
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 2 },
    fill: {
      type: 'gradient',
      gradient: { shadeIntensity: 1, opacityFrom: 0.25, opacityTo: 0.05, stops: [0, 100] },
    },
    colors: series.map(s => s.color),
    xaxis: {
      categories: data.map(d => d[xField]),
      labels: { style: { fontSize: '11px', fontFamily: "'Inter', sans-serif" } },
    },
    yaxis: {
      labels: {
        formatter: (v) => v >= 1000 ? `${(v / 1000).toFixed(1)}K` : Math.round(v),
        style: { fontSize: '11px' },
      },
    },
    tooltip: {
      x: { format: 'dd MMM yyyy' },
      y: { formatter: (v) => v?.toLocaleString('en-IN') },
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right',
      fontSize: '12px',
      fontWeight: 600,
      markers: { radius: 3 },
    },
    grid: { borderColor: '#E0E4E8', strokeDashArray: 4 },
  }), [data, xField, series, height])

  const chartSeries = useMemo(() =>
    series.map(s => ({
      name: s.label,
      data: data.map(d => d[s.key] ?? 0),
      type: s.type || 'area',
    })),
    [data, series]
  )

  useEffect(() => {
    if (!chartRef.current) return
    if (chartInstance.current) chartInstance.current.destroy()
    chartInstance.current = new ApexCharts(chartRef.current, { ...options, series: chartSeries })
    chartInstance.current.render()
    return () => { if (chartInstance.current) { chartInstance.current.destroy(); chartInstance.current = null } }
  }, [options, chartSeries])

  if (loading) {
    return (
      <Paper sx={{ p: 2.5 }}>
        {title && <Skeleton width={160} height={22} sx={{ mb: 2 }} />}
        <Skeleton variant='rectangular' height={height - 60} sx={{ borderRadius: 1 }} />
      </Paper>
    )
  }

  return (
    <Paper sx={{ p: 2.5 }}>
      {title && <Typography variant='subtitle1' sx={{ mb: 1 }}>{title}</Typography>}
      <Box>
        <div ref={chartRef} />
      </Box>
    </Paper>
  )
}
